import { User } from '../objects/user';
import { Version } from '../objects/version';
import { Approval } from '../objects/approval';
import { File } from '../objects/file';
import { Status } from '../objects/status';
/**
 * Updated 1st december 2023
 * Share feedback with this types:
 * - Approval 
 * - feedback (with only commnets)
 * - Upload Version (new)
 * Here we have the models  <diego@twonas.com> to 
 * fullfill the data objects managed for this features 
 */

export interface UploadVersionShare {
  origin: {
    version: Version,
    files: Array<File>,
    status: Status
  }
  upload: {
    version: Version,
    files: Array<File>
  }
}

export interface Entry {
  token: string;
  user: User;
}

/**
 * Enumeration of possible type values for version shares depending 
 * on the request mode done by user 
 *
 * @enum { string } 
 */
export enum VersionShareType {
  FEEDBACK_TYPE = "feedback", // only comments
  APPROVAL_TYPE = "approval", // approval and/or comments
  UPLOAD_VERSION_TYPE = "upload-version", // artwork request
}

/**
 * This contains the extra options needed for messages
 * @interface
 * case approval: 
 * @property message: the message leaved by the user\\
 * case upload version
 * @property version: the version string for the uploaded version
 * @property id_version: the version id for the uploaded version
 */
export interface VersionShareOptions {
  message?: string;
  id_version?: number;
  version?: string;
}
/**
 * General version share: used for fulfill the bubbles.
 * @type VersionShareType
 * @status VersionShareStatus
 * @entry Entry
 */
export interface VersionShare {
  // user?: any; in the entry
  // version?: Version; // not needed because the call is done for each version
  type: VersionShareType; // "feedback" | "approval" | "upload-version",
  status?: VersionShareStatus | null;  // be carefull in case of never null we could remove.
  entry: Entry;
  options: VersionShareOptions;
}

/**
 * Status defines depending on each VersionShare.type, the different ssituatiosn that the VersionShare
 * could have along
 */

export interface VersionShareStatus {
  date: string;
  status_code: number;  // 0: archived // 1: opened // 2: completed || approval status depending on type
  status_text?: string;
}

/**
 * oct-2026: one item per requested version, in the requested order, with the same list as
 * `GET /versions/{version_id}/shares`. Versions the user cannot see are left out.
 * An empty `shares` list can also mean the version's cache has not been built yet.
 * `[GET] /web/app/versions/shares?versions={id},{id},...`
 */
export interface VersionSharesByVersion {
  version: Pick<Version, 'id'>;
  shares: Array<VersionShare>;
}

/**
 * oct-2026: a bubble of a list page, replaces the legacy `VersionFeedback` of
 * `GET /p/feedback/versions/feedbacks/{ids}`. It is a `VersionShare` with the version it belongs to,
 * so lists holding entries of several versions can still be filtered by `version.id`.
 */
export interface VersionShareItem extends VersionShare {
  version: Pick<Version, 'id'>;
}

/**
 * oct-2026: one existing request that conflicts with a new Approval/Comments request
 * (one item per recipient and version; any request not archived conflicts, open or closed).
 * `entry.token` is `null` for requests older than shares.
 * `[POST] /web/app/shares/feedback/conflicts`, replaces the legacy `POST /p/feedback/versions/approvals`.
 */
export interface ShareFeedbackConflict {
  version: Pick<Version, 'id' | 'version'>;
  type: VersionShareType;
  entry: Entry;
  status: VersionShareStatus;
}

/**
 * Generic `{name, value}` item used by the share entry detail for its type-specific
 * `properties` and for the share's `options` and `properties`.
 * Lists instead of objects so the backend can add new items without changing the structure.
 */
export interface ShareNameValue<T = any> {
  name: string;
  value: T;
}

/**
 * Known names of `VersionShareEntryShare.options`.
 * Returned as stored: older shares may lack some of them (e.g. `get_signature`)
 * or carry legacy camelCase duplicates (e.g. `getApproval`).
 */
export enum VersionShareOptionName {
  ALLOW_SEE = "allow_see",
  GET_APPROVAL = "get_approval",
  GET_COMMENTS = "get_comments",
  GET_SIGNATURE = "get_signature",
}

/**
 * Known names of `VersionShareEntryShare.properties`. The list may grow.
 * `FILES` holds the legacy file ids shared with the recipient.
 */
export enum VersionShareSharePropertyName {
  EMAIL = "email",
  EMAIL_SUBJECT = "email_subject",
  EMAIL_CONTENT = "email_content",
  FILES = "files",
  REQUEST = "request",
  OPTIONS = "options",
}

/**
 * Known names of `VersionShareEntryDetail.properties`, depending on the entry type:
 * - approval: `MESSAGE` (recipient's comment)
 * - feedback: none
 * - upload-version: `ID_VERSION` and `VERSION` (null until the new version is uploaded)
 */
export enum VersionShareEntryPropertyName {
  MESSAGE = "message",
  ID_VERSION = "id_version",
  VERSION = "version",
}

/**
 * Summary of the share an entry belongs to, embedded in `VersionShareEntryDetail`.
 * A share has one entry per recipient, listed in `entries` (the requested one included).
 */
export interface VersionShareEntryShare {
  owner: Pick<User, 'id' | 'name' | 'email'>;
  date_created: string;
  entries: Array<Entry>;
  options: Array<ShareNameValue>;
  properties: Array<ShareNameValue>;
}

/**
 * One share entry of a version with the share it belongs to.
 * oct-2026: replaces the legacy share feedback response (`ShareVersionFeedback`).
 * `type`, `entry` and `status` are the same as in `VersionShare`; the list's `options`
 * object is replaced by `properties`.
 * `[GET] /web/app/versions/{version_id}/shares/{token}`
 */
export interface VersionShareEntryDetail {
  type: VersionShareType;
  entry: Entry;
  status: VersionShareStatus;
  properties: Array<ShareNameValue>;
  share: VersionShareEntryShare;
}

/**
 * Approval or Comments entry: the only types the legacy feedback endpoints returned.
 */
export function isFeedbackVersionShare(vs: Pick<VersionShare, 'type'>): boolean {
  return vs?.type === VersionShareType.APPROVAL_TYPE || vs?.type === VersionShareType.FEEDBACK_TYPE;
}

/**
 * `status_code` is the decision for approval entries (Pending means still open)
 * and open/closed for the other types.
 */
export function isVersionShareOpen(vs: Pick<VersionShare, 'type' | 'status'>): boolean {
  if (vs?.status == null)
    return false;
  switch (vs.type) {
    case VersionShareType.APPROVAL_TYPE:
      return vs.status.status_code == ApprovalStatusCodeEnum.PENDING_CODE;
    case VersionShareType.FEEDBACK_TYPE:
      return vs.status.status_code == FeedbackStatusCodeEnum.OPENED_CODE;
    case VersionShareType.UPLOAD_VERSION_TYPE:
      return vs.status.status_code == UploadVersionStatusCodeEnum.OPENED_CODE;
    default:
      return false;
  }
}

/**
 * Options are returned as stored: very old shares may have none, so a missing option counts as `false`.
 */
export function getVersionShareOption(detail: VersionShareEntryDetail, name: VersionShareOptionName): boolean {
  return detail?.share?.options?.find((o: ShareNameValue) => o.name === name)?.value === true;
}

/**
 * Legacy file ids shared with the recipient (they can belong to other versions shared together).
 */
export function getVersionShareFiles(detail: VersionShareEntryDetail): Array<number> {
  return detail?.share?.properties?.find((p: ShareNameValue) => p.name === VersionShareSharePropertyName.FILES)?.value ?? [];
}

// For old feedbacks.
export interface VersionFeedback {
    user: User;
    version: Version;
    approval?: Approval | null;
    status?: VersionFeedbackStatus | null;  
    // optional status!!  if not, check approval & set status open.
}
// status added in 10/08/2023 to include completed status when no approval
export interface VersionFeedbackStatus {
    date: string;
    status_code: number;  // 0: archived // 1: opened // 2: completed || aapproval status depending on type
    status_text?: string;
}

export interface ShareVersionFeedback extends VersionFeedback {
  share: any;
    // TODO: share should be like
    // {date_created: string
    // entry:  
    // message: string
    // options:    {allow_see: true, get_approval: true, get_comments: true, get_signature: true}
    // owner: user}

}
/**
 * For list of shares this is the menu item model
 * 
 */

export interface VersionShareListItemMenuClick {
    key: string;
    share: VersionShare;
    menuIdentifier?: string;
}

/**
 * Enumeration of possible `CODES` and `TEXTS` for version feedbacks "particular" status
 *
 * @enum { number } 
 */
export enum FeedbackStatusCodeEnum {
  ARCHIVED_CODE = 0, // Archived
  OPENED_CODE = 1, // Opened
  CLOSED_CODE = 2, // Completed
}

export enum FeedbackStatusTextEnum {
  ARCHIVED_TEXT = 'Archived',
  OPENED_TEXT = 'Reset',
  CLOSED_TEXT = 'Completed',
}

/**
 * Enumeration of possible `CODES` for upload version status
 *
 * @enum { number } 
 */
export enum UploadVersionStatusCodeEnum {
  ARCHIVED_CODE = 0, // Archived
  OPENED_CODE = 1, // Opened
  CLOSED_CODE = 2, // Completed
}

/**
 * Enumeration of possible `CODES` and `TEXTS` for version feedback approvals status
 *
 * @enum { number } 
 */
export enum ApprovalStatusCodeEnum {
  PENDING_CODE = 0,
  APPROVED_CODE = 1,
  REJECTED_CODE = 2
}

export enum ApprovalStatusTextEnum {
  PENDING_TEXT = 'Pending',
  APPROVED_TEXT = 'Approved',
  REJECTED_TEXT = 'Rejected',
}

//*********** VersionShare.status is represented by the bubbles with colors **************/
/**
 * Array of `COLORS` for version feedback approvals status according status
 * source ['#F6B273','#47B934','#F44848'];
 *  
 */
export const ApprovalStatusColorValues: { [key: number]: string } = {
  [ApprovalStatusCodeEnum.PENDING_CODE]: '#F6B273',    // Orange for Pending
  [ApprovalStatusCodeEnum.APPROVED_CODE]: '#47B934',   // Green for Approved
  [ApprovalStatusCodeEnum.REJECTED_CODE]: '#F44848',  // Red for Rejected
};

/**
 * Array of `COLORS` for version feedback (comments only) status according status
 * source ['silver','#2E49EB'];
 *  
 */
export const FeedbackStatusColorValues: { [key: number]: string } = {
  [FeedbackStatusCodeEnum.ARCHIVED_CODE]: 'black',    // TBD for ARCHIVED (NOT USED)
  [FeedbackStatusCodeEnum.OPENED_CODE]: 'silver',   // silver grey for opened
  [FeedbackStatusCodeEnum.CLOSED_CODE]: '#2E49EB',  // blue twona for Closed/coimpleted
};
/**
 * Array of `COLORS` for version upload request status according status
 * source ['silver','#B97DE8'];
 *  
 */
export const UploadVersionStatusColorValues: { [key: number]: string } = {
  [UploadVersionStatusCodeEnum.ARCHIVED_CODE]: 'black',    // TBD for ARCHIVED (NOT USED)
  [UploadVersionStatusCodeEnum.OPENED_CODE ]: 'grey',  // grey for pending to upload 
  [UploadVersionStatusCodeEnum.CLOSED_CODE ]: '#B97DE8',  // purple twona for uploaded version
}

